import React from 'react';
import { Checkbox } from 'components/atomic/atoms/checkbox';
import { useConsent } from 'contexts/consent';

export const TEST_IDS = {
  privacy:   'consent-privacy-checkbox',
  marketing: 'consent-marketing-checkbox',
} as const;

/**
 * ConsentSection
 *
 * Domain-aware section that renders the consent checkboxes for a form.
 * Must be rendered inside a `<ConsentProvider>`.
 *
 * Renders nothing when consent is not required for the current user.
 * State (checked / unchecked) lives in `ConsentContext` — the parent form
 * calls `useConsent().validate()` before submit without touching this component.
 *
 * @example
 * ```tsx
 * <ConsentProvider buttonText="Continue">
 *   <form onSubmit={handleSubmit}>
 *     <ConsentSection />
 *     <button type="submit">Continue</button>
 *   </form>
 * </ConsentProvider>
 * ```
 */
export function ConsentSection() {
  const {
    isRequired,
    privacyConsented,
    marketingConsented,
    onPrivacyChange,
    onMarketingChange,
    labels,
  } = useConsent();

  if (!isRequired) return null;

  return (
    <div>
      <Checkbox
        checked={privacyConsented}
        onChange={e => onPrivacyChange(e.target.checked)}
        label={labels.privacyPolicy}
        testId={TEST_IDS.privacy}
      />
      <Checkbox
        checked={marketingConsented}
        onChange={e => onMarketingChange(e.target.checked)}
        label={labels.marketingCommunications}
        testId={TEST_IDS.marketing}
      />
    </div>
  );
}
